/**
 * Push state resolver (Card 17).
 *
 * Collapses everything known about push on this device into exactly ONE of the five states
 * in `PushState`, in a strict precedence order. Kept pure and free of Capacitor imports so
 * the precedence — the part that actually regressed on this project — is unit-testable
 * without a device.
 *
 * The ordering is not cosmetic. Each rung answers a question the rung below it cannot:
 * a build with no Firebase config will ALSO report no token, and (because
 * `checkPushPermissionStatus` reports a plugin error as `denied`) may ALSO report a denied
 * permission. Checking prerequisites last would therefore blame the user for a build bug.
 * See GOOGLE_OAUTH_NATIVE_MOBILE_PLAN.md §13.6.
 */

import type {
  PushDiagnosticsInput,
  ResolvedPushState,
  PushServerSubscription
} from '@/types/pushDiagnostics.types';

/**
 * How long a registration may go without being re-synced before it is called stale.
 *
 * FCM rotates tokens. `initializePushNotifications()` re-syncs on every launch, so a device
 * that has been opened at all inside this window has a fresh token; one that has not is a
 * token nobody has confirmed still works.
 */
export const TOKEN_STALE_AFTER_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

function daysSince(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  return (now - then) / DAY_MS;
}

/** The active subscription the server holds for this device's platform, if any. */
function serverSubscriptionForPlatform(
  subscriptions: PushServerSubscription[],
  platform: string
): PushServerSubscription | null {
  return subscriptions.find((sub) => sub.platform === platform && sub.isActive) ?? null;
}

/**
 * Resolve the single state that describes this device.
 *
 * `now` is injectable so staleness can be tested without waiting a month.
 */
export function resolvePushState(
  input: PushDiagnosticsInput,
  now: number = Date.now()
): ResolvedPushState {
  const { prerequisites, permission, localToken, lastDelivery, server, isNative, platform } = input;

  // ── 1. Can this BUILD receive push at all? ────────────────────────────────
  // Highest precedence, and the reason this card exists. A misconfigured build must say so
  // in words instead of looking like an idle-but-healthy app.
  if (isNative && !prerequisites.supported && prerequisites.reason) {
    return {
      state: 'native_push_unavailable',
      headline: nativeUnavailableHeadline(prerequisites.reason),
      remedy: nativeUnavailableRemedy(prerequisites.reason),
      reason: prerequisites.reason,
      detail: prerequisites.detail
    };
  }

  // The device half can be perfect and still deliver nothing if the API has no credentials.
  // Same class of failure — a configuration problem that presents as silence — so it gets
  // the same state rather than being folded into "no token".
  if (server) {
    const needed = isNative ? server.server.fcmConfigured : server.server.webPushConfigured;
    if (!needed) {
      return {
        state: 'native_push_unavailable',
        headline: isNative
          ? 'The server has no Firebase credentials, so no push can be delivered.'
          : 'The server has no web push (VAPID) keys, so no push can be delivered.',
        remedy: isNative
          ? 'Set FIREBASE_PROJECT_ID, FIREBASE_CLIENT_EMAIL and FIREBASE_PRIVATE_KEY on the API.'
          : 'Set VAPID_PUBLIC_KEY and VAPID_PRIVATE_KEY on the API.',
        reason: 'firebase_config_missing',
        detail: null
      };
    }
  }

  // ── 2. Has the OS refused? ────────────────────────────────────────────────
  if (permission === 'denied') {
    return {
      state: 'permission_denied',
      headline: 'Notifications are turned off for this app.',
      remedy: isNative
        ? platform === 'ios'
          ? 'Open Settings → Notifications → Trade Loop and allow notifications.'
          : 'Open Settings → Apps → Trade Loop → Notifications and allow notifications.'
        : "Allow notifications for this site in your browser's site settings.",
      reason: null,
      detail: null
    };
  }

  // ── 3. Is there a registration at all? ────────────────────────────────────
  const serverSubscription = server
    ? serverSubscriptionForPlatform(server.subscriptions, platform)
    : null;

  if (!localToken.present && !serverSubscription) {
    return {
      state: 'no_token_yet',
      headline:
        permission === 'granted'
          ? 'Permission is granted but this device has not registered a push token yet.'
          : 'This device has not registered for push notifications yet.',
      remedy:
        permission === 'granted'
          ? 'Reopen the app to retry registration, or send a test to force one.'
          : 'Enable notifications to register this device.',
      reason: null,
      detail: null
    };
  }

  // ── 4. Is the registration current? ───────────────────────────────────────
  // Three distinct ways a token goes quietly dead, all of which look identical on the device.
  if (localToken.pendingSync) {
    return {
      state: 'token_stale',
      headline: 'This device has a token the server never received.',
      remedy: 'Reopen the app while online — the token is retried on launch.',
      reason: null,
      detail: null
    };
  }

  if (localToken.present && !serverSubscription && server) {
    return {
      state: 'token_stale',
      headline: 'This device has a token but the server has no active registration for it.',
      remedy: 'Reopen the app to re-register, or send a test to force a re-sync.',
      reason: null,
      detail: null
    };
  }

  if (
    localToken.suffix &&
    serverSubscription?.tokenSuffix &&
    localToken.suffix !== serverSubscription.tokenSuffix
  ) {
    return {
      state: 'token_stale',
      headline: 'The server holds a different token than this device — sends are going nowhere.',
      remedy: 'Reopen the app to re-sync this device’s current token.',
      reason: null,
      detail: null
    };
  }

  const ageDays = daysSince(
    serverSubscription?.lastSyncedAt || serverSubscription?.registeredAt,
    now
  );
  if (ageDays !== null && ageDays > TOKEN_STALE_AFTER_DAYS) {
    return {
      state: 'token_stale',
      headline: `The registered token has not been re-synced in ${Math.floor(ageDays)} days and may have rotated.`,
      remedy: 'Reopen the app to re-sync, then send a test to confirm delivery.',
      reason: null,
      detail: null
    };
  }

  // ── 5. Registered and healthy. ────────────────────────────────────────────
  // `delivered` describes the REGISTRATION, not proof of arrival: `lastDelivery` carries
  // that, and a healthy device that simply has not been sent anything yet is not a fault.
  const summary = server?.delivery.summaries.find((s) => s.platform === platform) ?? null;

  if (lastDelivery) {
    return {
      state: 'delivered',
      headline: `Registered and working — last notification arrived ${lastDelivery.receivedAt}.`,
      remedy: null,
      reason: null,
      detail: null
    };
  }

  if (summary?.lastSuccessAt) {
    return {
      state: 'delivered',
      headline:
        'Registered. The server has delivered to this platform, but this device has not seen a notification yet.',
      remedy: 'Send a test notification to confirm it arrives on this device.',
      reason: null,
      detail: null
    };
  }

  return {
    state: 'delivered',
    headline: 'Registered. No notification has been delivered to this device yet.',
    remedy: 'Send a test notification to exercise the whole path.',
    reason: null,
    detail: null
  };
}

function nativeUnavailableHeadline(reason: NonNullable<ResolvedPushState['reason']>): string {
  switch (reason) {
    case 'plugin_missing':
      return 'This build does not include the push notification plugin — push cannot work at all.';
    case 'firebase_config_missing':
      return 'This build is missing its Firebase configuration file — push cannot be registered.';
    case 'registration_failed':
      return 'Native push registration failed on this device.';
  }
}

function nativeUnavailableRemedy(reason: NonNullable<ResolvedPushState['reason']>): string {
  switch (reason) {
    case 'plugin_missing':
      // The §13.6 SPM trap: `cap sync` prints a warning, still LISTS the plugin under
      // "Found N plugins for ios", and omits it from the binary.
      return 'Rebuild the app with @capacitor/push-notifications installed. On iOS, confirm the plugin ships a Package.swift and appears in ios/App/CapApp-SPM/Package.swift after `cap sync`.';
    case 'firebase_config_missing':
      return 'Add google-services.json (Android) or GoogleService-Info.plist (iOS) to the build and reinstall.';
    case 'registration_failed':
      return 'Check the detail below, then reinstall the app. See src/docs/ANDROID_PUSH_SETUP.md / IOS_PUSH_SETUP.md.';
  }
}
